// Portal de facturación de la suscripción (solo AR) — pantalla PROPIA full-screen,
// sin el shell/sidebar de la app, con el layout del customer portal de Stripe pero
// diseño Atlas (colores, bordes redondeados). Columna izquierda = branding + Volver;
// columna derecha = el contenido (suscripción, método, datos fiscales, historial).
// El botón "Volver" hace navigate(-1) → devuelve a donde estabas (ej. Configuración),
// con el guard de ADR-144 para no salirse de Atlas cuando no hay historial in-app.
// Ver ADR-2026-08-03-facturacion-portal-stripe-completo y ADR-2026-08-07-facturacion-volver-loop.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { paisFlags, facturacionVisible } from '../lib/checkoutPlans';
import { useUserGroup } from '../hooks/useUserGroup';
import { entryStudioPath } from '../lib/userGroups';
import { FacturacionSuscripcion } from './config_sections/FacturacionSuscripcion';

export default function FacturacionPortal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const demo = searchParams.get('demo') !== null;
  const [idCompany, setIdCompany] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const { isDevTester, grupo } = useUserGroup();
  const homePath = entryStudioPath(isDevTester, grupo);
  // Contexto post-pago: el usuario llegó recién de pagar (vía la oferta de facturación), no desde
  // Configuración. En ese caso mostramos "Entrar a Atlas" en vez de "Volver" (que lo devolvería al
  // checkout, dejándolo sin puerta de entrada). Ver ADR-2026-08-13-facturacion-pantalla-post-pago-ar.
  //
  // Llega por QUERY PARAM (?postPago=1) para SOBREVIVIR un refresh/reopen del portal: el location.state
  // se pierde al recargar y dejaba "Volver" (→ /configuracion) en vez de "Entrar a Atlas". Aceptamos
  // también el state por compatibilidad. Greptile PR#67.
  const postPago =
    searchParams.get('postPago') !== null ||
    (location.state as { postPago?: boolean } | null)?.postPago === true;

  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (auth.user) {
        const { data: p } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', auth.user.id)
          .maybeSingle();
        const idc = (p as { id_company?: string | null } | null)?.id_company ?? null;
        setIdCompany(idc);
        // La facturación de la suscripción es SOLO Argentina. Si un usuario del exterior
        // llega a la ruta directo (el link del menú ya está limitado a AR), lo devolvemos
        // a la app en vez de mostrarle un portal que no le aplica. (El modo demo no gatea.)
        if (!demo && idc) {
          const { data: company } = await supabase
            .from('company_profiles')
            .select('pais, dev_tester')
            .eq('id_company', idc)
            .maybeSingle();
          const c = company as { pais?: string | null; dev_tester?: boolean | null } | null;
          const esAR = paisFlags(c?.pais).isArgentina;
          // Gate: solo AR, y solo si la facturación está habilitada (FACTURACION_ENABLED) o es
          // dev_tester. Oculta el portal a usuarios reales hasta que AFIP esté en prod (sigue en
          // homologación → facturas de prueba). Usamos el `dev_tester` AUTORITATIVO de la DB (no el de
          // useUserGroup, que en cache puede estar viejo/null y echaría a un dev tester válido antes de
          // resolver). Ver ADR-2026-08-13-facturacion-gate-dev-tester. Greptile PR#69.
          if (!facturacionVisible(esAR, c?.dev_tester === true)) {
            navigate('/', { replace: true });
            return;
          }
        }
      }
      setReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // POP (vuelve a donde estabas: Configuración, éxito de suscripción, lo que sea), pero nunca
  // a ciegas — guard de ADR-144. `key === 'default'` = deep-link/refresh directo en /facturacion:
  // no hay historial in-app y el back se iría fuera de Atlas. `state.sinHistorial` es el mismo
  // caso llegando desde el fallback del detalle (ahí el replace ya le dio una key propia).
  const volver = () => {
    if (location.key !== 'default' && !(location.state as { sinHistorial?: boolean } | null)?.sinHistorial) {
      navigate(-1);
      return;
    }
    navigate('/configuracion', { replace: true });
  };

  return (
    <div className="min-h-screen bg-surface-bg md:flex">
      {/* Columna izquierda — branding + Volver (en desktop, columna fija; en mobile, barra superior) */}
      {/* `pt-[calc(env(safe-area-inset-top)…)]`: esta pantalla vive FUERA del shell
          (bloque "Protegidas SIN sidebar"), así que nadie le compensa la status bar
          translúcida del PWA standalone — sin esto, "Atlas" y "Volver" quedaban
          debajo de la hora (mismo arreglo que el header de /checkout). En desktop
          `md:py-10` pisa el calc; en browser mobile el inset es 0 y queda el py-4
          de siempre. */}
      <aside className="flex md:flex-col justify-between md:justify-start items-center md:items-start gap-4 px-5 pb-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] md:h-screen md:sticky md:top-0 md:w-[320px] md:shrink-0 md:px-9 md:py-10">
        <div className="flex items-center gap-2.5">
          <img src="/favicon.png" alt="Atlas" className="w-7 h-7 rounded-md" />
          <span className="text-[15px] font-semibold text-brand-black/90">Atlas</span>
        </div>

        <div className="hidden md:block mt-12">
          <h1 className="text-[26px] leading-[1.15] font-semibold text-brand-black/90 text-balance">
            Facturación<br />de tu plan
          </h1>
          <p className="text-[14px] leading-snug text-neutral-600 mt-3 max-w-[250px]">
            Gestioná tu suscripción y descargá las facturas AFIP de Atlas.
          </p>
        </div>

        {postPago ? (
          <button
            onClick={() => navigate(homePath, { replace: true })}
            className="inline-flex items-center gap-1.5 md:mt-8 h-9 px-3.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-medium transition-colors self-start"
          >
            Entrar a Atlas <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={volver}
            className="inline-flex items-center gap-1.5 md:mt-8 h-9 rounded-md text-[14px] font-medium text-brand-black/70 hover:text-brand-black transition-colors self-start"
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
        )}
      </aside>

      {/* Columna derecha — el contenido del portal */}
      <main className="flex-1 min-w-0 md:border-l md:border-black/[0.07] bg-surface-card min-h-screen">
        <div className="max-w-2xl mx-auto px-5 py-6 md:px-10 md:py-12">
          {/* En mobile el título va arriba del contenido (en desktop está en la columna izquierda) */}
          <h1 className="md:hidden text-[22px] font-semibold text-brand-black/90 mb-1">Facturación de tu plan</h1>
          <p className="md:hidden text-[13.5px] text-neutral-500 mb-6">Tu suscripción y las facturas AFIP de tu plan.</p>

          {ready || demo ? (
            <FacturacionSuscripcion profile={{ id_company: idCompany }} demo={demo} />
          ) : (
            <div className="h-40" />
          )}
        </div>
      </main>
    </div>
  );
}
